import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, AlertCircle, Loader2, Calendar, TrendingDown, BarChart3, Bell, MessageCircle } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { trackEvent } from '../utils/analytics';
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    if (!supabase) {
      setError('Supabase is not configured');
      setIsLoading(false);
      return;
    }
    try {
      const {
        data,
        error: signInError
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (signInError) {
        setError(signInError.message);
        trackEvent('Login Failed', {
          error: signInError.message
        });
      } else {
        trackEvent('Login Success', {
          email
        });
        // Redirect to dashboard after successful login
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError('An unexpected error occurred');
      trackEvent('Login Error', {
        error: String(err)
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleWhatsAppContact = () => {
    trackEvent('WhatsApp Contact Click', {
      source: 'login_page'
    });
    window.open('https://wa.me/2250700592164?text=Bonjour,%20je%20souhaite%20ouvrir%20un%20compte%20business%20pour%20gérer%20mes%20abonnements', '_blank');
  };
  const features = [{
    icon: Calendar,
    title: 'Calendrier des paiements',
    description: 'Visualisez tous vos abonnements dans un calendrier style Google Calendar',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  }, {
    icon: TrendingDown,
    title: 'Comparaison des providers',
    description: 'Comparez automatiquement Wave, Djamo, Push CI et trouvez le moins cher',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50'
  }, {
    icon: BarChart3,
    title: 'Économies calculées',
    description: 'Découvrez combien vous économisez en choisissant le meilleur provider',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  }, {
    icon: Bell,
    title: 'Alertes intelligentes',
    description: 'Recevez des rappels avant chaque paiement à venir',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex" data-id="element-540">
      {/* Left Side - Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-emerald-600 p-12 flex-col justify-between" data-id="element-541">
        <div data-id="element-542">
          <h1 className="text-4xl font-bold text-white mb-3" data-id="element-543">
            AfriCard Compare
          </h1>
          <p className="text-blue-100 text-lg" data-id="element-544">
            Gérez vos abonnements et économisez de l'argent
          </p>
        </div>

        <div className="space-y-6" data-id="element-545">
          <h2 className="text-2xl font-bold text-white mb-6" data-id="element-546">
            Fonctionnalités principales
          </h2>
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: index * 0.1
        }} className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-547">
              <div className={`w-12 h-12 ${feature.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`} data-id="element-548">
                <feature.icon className={`w-6 h-6 ${feature.color}`} data-id="element-549" />
              </div>
              <div data-id="element-550">
                <h3 className="text-white font-semibold mb-1" data-id="element-551">
                  {feature.title}
                </h3>
                <p className="text-blue-100 text-sm" data-id="element-552">{feature.description}</p>
              </div>
            </motion.div>)}
        </div>

        <div className="text-blue-100 text-sm" data-id="element-553">
          © 2024 AfriCard Compare. Tous droits réservés.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12" data-id="element-554">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="w-full max-w-md" data-id="element-555">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8" data-id="element-556">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-557">
              AfriCard Compare
            </h1>
            <p className="text-gray-600" data-id="element-558">Connectez-vous à votre compte</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8" data-id="element-559">
            <div className="hidden lg:block mb-6" data-id="element-560">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-561">
                Connexion
              </h2>
              <p className="text-gray-600" data-id="element-562">Accédez à votre dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6" data-id="element-563">
              {/* Error Message */}
              {error && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-id="element-564">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-565" />
                  <div data-id="element-566">
                    <p className="text-sm font-medium text-red-800" data-id="element-567">
                      Échec de connexion
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-568">{error}</p>
                  </div>
                </motion.div>}

              {/* Email Field */}
              <div data-id="element-569">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-570">
                  Email
                </label>
                <div className="relative" data-id="element-571">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-572" />
                  <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" placeholder="vous@exemple.com" disabled={isLoading} data-id="element-573" />
                </div>
              </div>

              {/* Password Field */}
              <div data-id="element-574">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-575">
                  Mot de passe
                </label>
                <div className="relative" data-id="element-576">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-577" />
                  <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" placeholder="••••••••" disabled={isLoading} data-id="element-578" />
                </div>
              </div>

              {/* Submit Button */}
              <button type="submit" disabled={isLoading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-579">
                {isLoading ? <>
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-580" />
                    Connexion...
                  </> : 'Se connecter'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6" data-id="element-581">
              <div className="absolute inset-0 flex items-center" data-id="element-582">
                <div className="w-full border-t border-gray-200" data-id="element-583"></div>
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-584">
                <span className="px-4 bg-white text-gray-500" data-id="element-585">
                  Pas encore de compte ?
                </span>
              </div>
            </div>

            {/* WhatsApp Contact Button */}
            <button onClick={handleWhatsAppContact} className="w-full bg-emerald-600 text-white py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2" data-id="element-586">
              <MessageCircle className="w-5 h-5" data-id="element-587" />
              Ouvrir un compte business
            </button>

            {/* Footer Links */}
            <div className="mt-6 text-center" data-id="element-588">
              <a href="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-589">
                ← Retour à l'accueil
              </a>
            </div>
          </div>

          {/* Mobile Features */}
          <div className="lg:hidden mt-8 space-y-4" data-id="element-590">
            {features.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5 + index * 0.1
          }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-id="element-591">
                <div className="flex items-start gap-3" data-id="element-592">
                  <div className={`w-10 h-10 ${feature.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`} data-id="element-593">
                    <feature.icon className={`w-5 h-5 ${feature.color}`} data-id="element-594" />
                  </div>
                  <div data-id="element-595">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-596">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-xs" data-id="element-597">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}